import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useUpsertZendeskIntegrationMutation } from "modules/settings/hooks/zendesk/useUpsertZendeskIntegrationMutation"
import { useZendeskIntegrationQuery } from "modules/settings/hooks/zendesk/useZendeskIntegrationQuery"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { AuthEmployeeResponseData, UpsertZendeskIntegrationSchema } from "schema"
import { toast } from "sonner"
import z from "zod"

function buildEmptyValues(employeeData: AuthEmployeeResponseData | undefined) {
  return {
    companySubdomain: employeeData?.company?.name?.toLowerCase().replace(" ", "") ?? "",
    token: "",
    email: "",
  }
}

export const ZendeskIntegrationModal = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: employeeData } = useAuthEmployeeQuery() ?? {}
  const { mutateAsync, isPending } = useUpsertZendeskIntegrationMutation()

  const { data, isPending: isZendeskIntegrationQueryPending } = useZendeskIntegrationQuery({
    onSuccess: (data) => {
      form.setValue(
        "companySubdomain",
        data.data?.companySubdomain ??
          employeeData?.company?.name?.toLowerCase().replace(" ", "") ??
          "",
      )
      form.setValue("email", data.data?.email ?? employeeData?.email ?? "")
    },
  })

  const form = useForm<z.infer<typeof UpsertZendeskIntegrationSchema>>({
    resolver: zodResolver(UpsertZendeskIntegrationSchema),
    defaultValues: data ?? buildEmptyValues(employeeData),
    mode: "all",
  })

  function onOpenChange(isOpen: boolean = false) {
    if (isPending) return

    if (!isOpen) {
      navigate({ to: "/settings/integrations/zendesk" })
    }
  }

  async function onSubmit(data: z.infer<typeof UpsertZendeskIntegrationSchema>) {
    await mutateAsync(data)
    toast.success(t("settings.integrations.zendesk.integrationCard.success"))
    onOpenChange(false)
  }

  if (isZendeskIntegrationQueryPending) return null

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader className="flex flex-col gap-2 items-start">
          <DialogTitle>{t("settings.integrations.zendesk.integrationCard.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.integrations.zendesk.integrationCard.description")}{" "}
            <Popover>
              <PopoverTrigger slick>
                <span>{t("settings.integrations.zendesk.integrationCard.help.tooltip")}</span>
              </PopoverTrigger>
              <PopoverContent className="max-w-96 flex flex-col gap-2">
                <h3 className="font-medium">
                  {t("settings.integrations.zendesk.integrationCard.help.tooltip")}
                </h3>
                <ul className="list-decimal list-inside text-left text-sm space-y-1">
                  <li>{t("settings.integrations.zendesk.integrationCard.help.step1")}</li>
                  <li>{t("settings.integrations.zendesk.integrationCard.help.step2")}</li>
                  <li>{t("settings.integrations.zendesk.integrationCard.help.step3")}</li>
                  <li>{t("settings.integrations.zendesk.integrationCard.help.step4")}</li>
                  <li>{t("settings.integrations.zendesk.integrationCard.help.step5")}</li>
                </ul>
              </PopoverContent>
            </Popover>
          </DialogDescription>
        </DialogHeader>
        <div>
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <FormField
              control={form.control}
              name="companySubdomain"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t("settings.integrations.zendesk.integrationCard.form.companySubdomain")}
                  </FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-0">
                      <Input
                        {...field}
                        required
                        placeholder={employeeData?.company?.name?.toLowerCase().replace(" ", "")}
                        className="rounded-none"
                        leftText="https://"
                        rightText=".zendesk.com"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="token"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t("settings.integrations.zendesk.integrationCard.form.token")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} required placeholder="1234567890" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t("settings.integrations.zendesk.integrationCard.form.email")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} required placeholder={employeeData?.email} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button disabled={form.formState.isLoading || !form.formState.isValid || isPending}>
                {t("settings.integrations.zendesk.integrationCard.form.submit")}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
                {t("shared.cancel")}
              </Button>
            </DialogFooter>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
